/**
 * AdminPoopartnerIntakesPage — `/admin/poopartner-intakes`
 *
 * Phase 1c admin queue. Table list of intakes with a status filter,
 * click-to-open detail modal with Approve / Reject / Reveal EIN /
 * Download W-9 actions. All mutations go through backend routes
 * shipped in Phase 1b and are audit-logged server-side.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, API_BASE, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Eye, FileDown, Check, X, Copy } from "lucide-react";

const STATUS_OPTIONS = [
  { id: "queue", label: "Queue (Submitted + Under Review)", api: "submitted,under_review" },
  { id: "submitted", label: "Submitted", api: "submitted" },
  { id: "under_review", label: "Under Review", api: "under_review" },
  { id: "approved", label: "Approved", api: "approved" },
  { id: "rejected", label: "Rejected", api: "rejected" },
];

const EIN_REVEAL_AUTOHIDE_MS = 30_000;

export default function AdminPoopartnerIntakesPage() {
  const [filterId, setFilterId] = useState("queue");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [einValue, setEinValue] = useState(null);
  const [einLoading, setEinLoading] = useState(false);

  const currentFilter = STATUS_OPTIONS.find((s) => s.id === filterId);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/poopartner/intakes", {
        params: { status: currentFilter.api },
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [currentFilter.api]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setEinValue(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/admin/poopartner/intakes/${selectedId}`);
        if (!cancelled) setDetail(data);
      } catch (e) {
        if (!cancelled) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  // EIN auto-hide timer
  useEffect(() => {
    if (!einValue) return undefined;
    const t = setTimeout(() => setEinValue(null), EIN_REVEAL_AUTOHIDE_MS);
    return () => clearTimeout(t);
  }, [einValue]);

  const approve = async () => {
    if (!selectedId || approving) return;
    setApproving(true);
    try {
      await api.post(`/admin/poopartner/intakes/${selectedId}/approve`);
      toast.success("Application approved.");
      setSelectedId(null);
      fetchList();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setApproving(false);
    }
  };

  const reject = async () => {
    if (!selectedId || rejecting) return;
    if (rejectReason.trim().length < 5) return;
    setRejecting(true);
    try {
      await api.post(`/admin/poopartner/intakes/${selectedId}/reject`, {
        reason: rejectReason.trim(),
      });
      toast.success("Application rejected.");
      setRejectOpen(false);
      setRejectReason("");
      setSelectedId(null);
      fetchList();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setRejecting(false);
    }
  };

  const revealEin = async () => {
    if (!selectedId || einLoading) return;
    setEinLoading(true);
    try {
      const { data } = await api.get(
        `/admin/poopartner/intakes/${selectedId}/ein-reveal`,
      );
      setEinValue(data.ein_formatted);
    } catch (e) {
      if (e.response?.status === 429) {
        toast.error("EIN reveal rate-limited. Try again in a minute.");
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    } finally {
      setEinLoading(false);
    }
  };

  const downloadW9 = async () => {
    if (!selectedId) return;
    try {
      const token = localStorage.getItem("pp_token");
      const resp = await fetch(
        `${API_BASE}/admin/poopartner/intakes/${selectedId}/w9`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `w9_${selectedId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("W-9 downloaded.");
    } catch (e) {
      toast.error("Couldn't download the W-9.");
    }
  };

  return (
    <div data-testid="admin-poopartner-intakes-page" className="space-y-6">
      <header className="flex flex-wrap gap-3 items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-pp-text">
            PooPartner intake queue
          </h1>
          <p className="text-sm text-pp-text-muted mt-1">
            Approve / reject business verifications, reveal EIN, download W-9s.
          </p>
        </div>
        <div className="min-w-[240px]">
          <Label>Status filter</Label>
          <Select value={filterId} onValueChange={setFilterId}>
            <SelectTrigger data-testid="admin-intake-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem
                  key={s.id}
                  value={s.id}
                  data-testid={`admin-intake-status-filter-option-${s.id}`}
                >
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </header>

      <div
        className="rounded border border-pp-border bg-white overflow-hidden"
        data-testid="admin-intake-queue-table"
      >
        <table className="w-full text-sm">
          <thead className="bg-pp-surface-2">
            <tr>
              <th className="p-3 text-left">Business</th>
              <th className="p-3 text-left">Entity</th>
              <th className="p-3 text-left">Revenue</th>
              <th className="p-3 text-left">Submitted</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="6" className="p-6 text-center text-pp-text-muted">
                  <Loader2 className="animate-spin inline-block" size={18} />
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td
                  colSpan="6"
                  className="p-6 text-center text-pp-text-muted"
                  data-testid="admin-intake-queue-empty"
                >
                  No intakes in this filter.
                </td>
              </tr>
            )}
            {!loading &&
              items.map((it) => (
                <tr
                  key={it.id}
                  className="border-t border-pp-border hover:bg-pp-surface-2 cursor-pointer"
                  onClick={() => setSelectedId(it.id)}
                  data-testid={`admin-intake-row-${it.id}`}
                >
                  <td className="p-3">{it.business_name}</td>
                  <td className="p-3">{it.entity_type}</td>
                  <td className="p-3">{it.revenue_band}</td>
                  <td className="p-3">
                    {new Date(it.submitted_at).toLocaleDateString()}
                  </td>
                  <td className="p-3 capitalize">{it.status.replace("_", " ")}</td>
                  <td className="p-3 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedId(it.id);
                      }}
                      data-testid={`admin-intake-open-btn-${it.id}`}
                    >
                      Open
                    </Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selectedId} onOpenChange={(v) => !v && setSelectedId(null)}>
        <DialogContent
          className="max-w-2xl"
          data-testid="admin-intake-detail-modal"
        >
          <DialogHeader>
            <DialogTitle>Intake detail</DialogTitle>
          </DialogHeader>
          {detailLoading && (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin" size={22} />
            </div>
          )}
          {detail && (
            <div className="space-y-3 text-sm">
              <div>
                <span className="text-pp-text-muted">Business:</span>{" "}
                <span
                  className="font-medium text-pp-text"
                  data-testid="admin-intake-detail-business-name"
                >
                  {detail.business_name}
                </span>{" "}
                <span className="text-pp-text-muted">
                  ({detail.legal_entity_type})
                </span>
              </div>
              <div>
                <span className="text-pp-text-muted">EIN:</span>{" "}
                <span className="font-mono">XX-XXX{detail.ein_last4}</span>
                {einValue && (
                  <span
                    className="ml-3 font-mono text-pp-orange"
                    data-testid="admin-intake-ein-revealed"
                  >
                    {einValue}
                    <button
                      type="button"
                      className="ml-2 text-pp-text-muted hover:text-pp-text"
                      onClick={() => {
                        navigator.clipboard?.writeText(einValue);
                        toast.success("EIN copied.");
                      }}
                      aria-label="Copy EIN"
                      data-testid="admin-intake-ein-copy-btn"
                    >
                      <Copy size={14} className="inline-block" />
                    </button>
                  </span>
                )}
              </div>
              <div>
                <span className="text-pp-text-muted">Revenue band:</span>{" "}
                {detail.revenue_band}
              </div>
              <div>
                <span className="text-pp-text-muted">Status:</span>{" "}
                <span className="capitalize" data-testid="admin-intake-detail-status">
                  {detail.status.replace("_", " ")}
                </span>
              </div>
              <div>
                <span className="text-pp-text-muted">Captcha:</span>{" "}
                {detail.captcha_status}
              </div>
              <div>
                <span className="text-pp-text-muted">Legal:</span> version{" "}
                {detail.legal_version_id}, accepted from IP{" "}
                {detail.accepted_ip || "-"}
              </div>
              {detail.rejection_reason && (
                <div className="border border-red-200 bg-red-50 rounded p-2 text-red-800">
                  <strong>Reason:</strong> {detail.rejection_reason}
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex flex-wrap gap-2 justify-end mt-4">
            <Button
              variant="outline"
              onClick={revealEin}
              disabled={einLoading}
              data-testid="admin-intake-reveal-ein-btn"
            >
              {einLoading ? (
                <Loader2 className="animate-spin mr-1" size={14} />
              ) : (
                <Eye size={14} className="mr-1" />
              )}
              Reveal EIN
            </Button>
            <Button
              variant="outline"
              onClick={downloadW9}
              data-testid="admin-intake-download-w9-btn"
            >
              <FileDown size={14} className="mr-1" />
              Download W-9
            </Button>
            {detail && ["submitted", "under_review"].includes(detail.status) && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setRejectOpen(true)}
                  data-testid="admin-intake-reject-open-btn"
                >
                  <X size={14} className="mr-1" />
                  Reject
                </Button>
                <Button
                  onClick={approve}
                  disabled={approving}
                  data-testid="admin-intake-approve-btn"
                >
                  {approving ? (
                    <Loader2 className="animate-spin mr-1" size={14} />
                  ) : (
                    <Check size={14} className="mr-1" />
                  )}
                  Approve
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent
          className="max-w-md"
          data-testid="admin-intake-reject-modal"
        >
          <DialogHeader>
            <DialogTitle>Reject application</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label htmlFor="reject-reason">Reason (visible to the partner)</Label>
            <Textarea
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              minLength={5}
              maxLength={500}
              rows={5}
              placeholder="At least 5 characters"
              data-testid="admin-intake-reject-reason-textarea"
            />
            <p className="text-xs text-pp-text-muted">
              {rejectReason.trim().length}/500 characters
            </p>
          </div>
          <DialogFooter className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setRejectOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={reject}
              disabled={rejectReason.trim().length < 5 || rejecting}
              data-testid="admin-intake-reject-submit-btn"
            >
              {rejecting && (
                <Loader2 className="animate-spin mr-1" size={14} />
              )}
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
